import { cn } from "@/lib/utils"
import { CornerPlus } from "@/components/ui/corner-plus"
import { LazyDither } from "@/components/homepage/sections/LazyDither"
import {
  Video,
  Sparkles,
  Mic,
  MonitorPlay,
  Film,
  User,
  Zap,
  MessageSquare,
  ArrowRight,
} from "lucide-react"

// ─── Data ────────────────────────────────────────────────────────────────────

const videoModels = [
  {
    name: "Kling V3 Pro",
    provider: "Kuaishou",
    tags: ["Text-to-Video", "Image-to-Video"],
    description:
      "High-fidelity video generation with exceptional motion quality and cinematic output.",
    icon: Video,
    accent: false,
  },
  {
    name: "Veo 3.1",
    provider: "Google DeepMind",
    tags: ["Text-to-Video", "Image-to-Video"],
    description:
      "Google's frontier video model with photorealistic quality and natural physics.",
    icon: MonitorPlay,
    accent: true,
  },
  {
    name: "Kling O3 Pro",
    provider: "Kuaishou",
    tags: ["Text-to-Video", "Image-to-Video"],
    description:
      "Optimized for fast iteration with strong prompt adherence and consistent style.",
    icon: Film,
    accent: true,
  },
  {
    name: "Grok Imagine Video",
    provider: "xAI",
    tags: ["Text-to-Video"],
    description:
      "Creative video generation with distinctive artistic style from xAI's Grok platform.",
    icon: Zap,
    accent: false,
  },
]

const agentCapabilities = [
  {
    icon: MessageSquare,
    title: "Narrative structuring",
    description:
      "Breaks your concept into characters, settings, mood, and pacing.",
  },
  {
    icon: Sparkles,
    title: "Shot-by-shot prompts",
    description:
      "Generates model-optimized prompts with camera angles, lighting, and motion.",
  },
  {
    icon: Video,
    title: "Iterative refinement",
    description:
      "Adjusts creative direction and regenerates until every frame matches your vision.",
  },
]

const voiceIntegrations = [
  {
    name: "ElevenLabs TTS",
    description:
      "Natural text-to-speech with expressive voices across 30+ languages.",
    icon: Mic,
  },
  {
    name: "Veed Fabric",
    description:
      "Photorealistic AI avatars with native lip sync from a single image and audio.",
    icon: User,
  },
]

// ─── Main Export ─────────────────────────────────────────────────────────────

export function IntegrationsContent() {
  return (
    <>
      {/* ── Section 1: AI Video Models ── */}
      <section className="w-full relative">
        <div className="mx-auto">
          <div className="grid md:grid-cols-6 divide-x divide-border">
            {/* Left — title */}
            <div className="col-span-2 flex flex-col gap-4 p-8 md:p-14">
              <div className="space-y-4">
                <h3 className="text-3xl font-medium tracking-tighter md:text-4xl">
                  Four frontier models, one studio
                </h3>
                <p className="text-balance text-muted-foreground">
                  Choose the right model for every shot. Switch between them in a single timeline.
                </p>
              </div>
            </div>

            {/* Right — model cards */}
            <div className="col-span-4 flex flex-col">
              <div className="relative h-14 border-b overflow-hidden">
                <div className="absolute inset-0">
                  <LazyDither enableMouseInteraction={false} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2">
                {videoModels.map((model, i) => {
                  return (
                    <div
                      key={model.name}
                      className={cn(
                        "flex flex-col gap-6 p-6 md:p-8",
                        model.accent ? "bg-accent" : "bg-card/50",
                        i % 2 === 0 && "border-b md:border-r border-border",
                        i % 2 === 1 && "border-b border-border",
                      )}
                    >
                      <div className="flex flex-col gap-6">
                        <h4 className="text-xl font-medium tracking-tight">
                          {model.name}
                        </h4>
                        <div className="flex flex-col gap-2">
                          <p className="text-sm text-muted-foreground">
                            {model.provider}
                          </p>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {model.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-auto">
                        {model.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center text-xs font-medium text-muted-foreground bg-background/60 px-2.5 py-1 rounded-full border border-border/50"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="relative h-14 border-t overflow-hidden">
                <div className="absolute inset-0">
                  <LazyDither enableMouseInteraction={false} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 2: AI Creative Direction + Voice ── */}
      <section className="w-full relative">
        <div className="mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-6">
            {/* Left — sticky title */}
            <div className="col-span-1 md:col-span-2 p-8 md:p-10 lg:p-14 md:sticky md:top-20 md:self-start flex flex-col gap-7">
              <h3 className="text-3xl lg:text-4xl font-medium tracking-tighter text-left text-balance">
                AI that directs, voices, and performs
              </h3>
              <p className="text-muted-foreground text-left text-balance">
                Claude writes creative briefs and generates shot-by-shot prompts. ElevenLabs adds voiceover. Kling syncs lips and creates avatars.
              </p>
            </div>

            {/* Right — content blocks */}
            <div className="col-span-1 md:col-span-4 w-full border-t md:border-t-0 md:border-l border-border relative">
              <CornerPlus position="all" className="text-muted-foreground/50" />
              <div className="w-full divide-y divide-border">
                {/* Claude agent block */}
                <div className="relative">
                  <div className="min-h-[280px] md:min-h-[340px] flex items-center justify-center p-8 md:p-12 overflow-hidden">
                    <div className="w-full max-w-md">
                      {/* Mini agent flow visualization */}
                      <div className="rounded-2xl border border-border bg-card p-5 space-y-4 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-full bg-foreground flex items-center justify-center">
                            <Sparkles className="size-4 text-background" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Claude</p>
                            <p className="text-xs text-muted-foreground">Creative Director</p>
                          </div>
                        </div>
                        <div className="space-y-2.5">
                          {agentCapabilities.map((cap) => {
                            return (
                              <div key={cap.title} className="flex items-start gap-3 pl-1">
                                <ArrowRight className="size-3.5 text-muted-foreground mt-0.5 shrink-0" />
                                <div>
                                  <p className="text-sm font-medium">{cap.title}</p>
                                  <p className="text-xs text-muted-foreground">{cap.description}</p>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="max-w-xl text-left items-start p-6">
                    <p className="text-sm text-muted-foreground flex items-center gap-3 justify-start">
                      <Sparkles className="size-4 text-muted-foreground" />
                      Powered by Anthropic Claude
                    </p>
                    <p className="text-base text-foreground leading-relaxed mt-2">
                      Your AI creative partner analyzes your concept, structures the narrative, and generates detailed prompts for every clip in your timeline.
                    </p>
                  </div>
                </div>

                {/* Voice & Audio block */}
                <div className="relative">
                  <CornerPlus position="top-left" className="text-muted-foreground/50" />
                  <CornerPlus position="top-right" className="text-muted-foreground/50" />
                  <div className="min-h-[240px] md:min-h-[300px] flex items-center justify-center p-8 md:p-12">
                    <div className="w-full max-w-md space-y-3">
                      {voiceIntegrations.map((item) => {
                        const Icon = item.icon
                        return (
                          <div
                            key={item.name}
                            className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card transition-colors"
                          >
                            <div className="size-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                              <Icon className="size-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{item.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                            </div>
                            <span className="text-xs text-primary font-medium bg-primary/10 rounded-lg px-2 py-1 shrink-0">
                              Connected
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  <div className="max-w-xl text-left items-start p-6">
                    <p className="text-sm text-muted-foreground flex items-center gap-3 justify-start">
                      <Mic className="size-4 text-muted-foreground" />
                      Voice, lip sync, and AI avatars
                    </p>
                    <p className="text-base text-foreground leading-relaxed mt-2">
                      Add natural voiceovers, synchronize lip movements, and create AI avatars that speak and express — all directly in your timeline.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
